<template>

   <!-- ── Summary Cards ── -->
   <div class="pp-summary-row">
      <div class="pp-stat-card">
         <div class="pp-stat-icon" style="background: linear-gradient(135deg, #00A0C1, #33b5d1);">
            <i class="fas fa-box-open"></i>
         </div>
         <div class="pp-stat-body">
            <span class="pp-stat-value">{{ allProductList.length }}</span>
            <span class="pp-stat-label">Total Products</span>
         </div>
      </div>
      <div class="pp-stat-card">
         <div class="pp-stat-icon" style="background: linear-gradient(135deg, #10b981, #34d399);">
            <i class="fas fa-hand-holding-heart"></i>
         </div>
         <div class="pp-stat-body">
            <span class="pp-stat-value">{{ charityProductCount }}</span>
            <span class="pp-stat-label">Charity Products</span>
         </div>
      </div>
      <div class="pp-stat-card">
         <div class="pp-stat-icon" style="background: linear-gradient(135deg, #3b82f6, #60a5fa);">
            <i class="fas fa-link"></i>
         </div>
         <div class="pp-stat-body">
            <span class="pp-stat-value">{{ linkedProductCount }}</span>
            <span class="pp-stat-label">Linked Products</span>
         </div>
      </div>
      <div class="pp-stat-card">
         <div class="pp-stat-icon" style="background: linear-gradient(135deg, #f59e0b, #fbbf24);">
            <i class="fas fa-dollar-sign"></i>
         </div>
         <div class="pp-stat-body">
            <span class="pp-stat-value">{{ avgPrice }}</span>
            <span class="pp-stat-label">Avg Price</span>
         </div>
      </div>
   </div>

   <!-- ── Main Table Card ── -->
   <div class="pp-card">
      <div class="pp-card__toolbar">
         <div class="pp-search-wrap">
            <input v-model.trim="search" type="text" class="pp-search-input"
               placeholder="🔍 Search products..."
               @keyup="onSearch()" />
         </div>

         <div class="pp-toolbar-right">
            <div class="pp-toolbar-group">
               <span class="pp-toolbar-label">Sort</span>
               <select v-model="sortBy" class="pp-select">
                  <option v-for="sortOption in sortByList" :value="sortOption.value" :key="sortOption.value">
                     {{ sortOption.text }}
                  </option>
               </select>
            </div>
            <div class="pp-toolbar-group">
               <span class="pp-toolbar-label">Rows</span>
               <select v-model="pagination.pageSize" class="pp-select pp-select--narrow">
                  <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
               </select>
            </div>
            <div class="pp-pagination">
               <button @click="previousPage()" :disabled="pagination.page === 1" class="pp-page-btn">
                  <i class="fas fa-chevron-left"></i>
               </button>
               <span class="pp-page-label">
                  <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
               </span>
               <button @click="nextPage()" :disabled="pagination.page === pagination.totalPages" class="pp-page-btn">
                  <i class="fas fa-chevron-right"></i>
               </button>
            </div>
         </div>
      </div>

      <!-- Table -->
      <div class="pp-table-wrap">
         <table class="pp-table">
            <thead>
               <tr>
                  <th v-for="header in filteredHeaders" :key="header.value">{{ header.text }}</th>
               </tr>
            </thead>

            <tbody v-if="!productList.length">
               <tr>
                  <td :colspan="filteredHeaders.length" class="pp-empty-row">
                     <div class="pp-empty-state">
                        <i class="fas fa-inbox"></i>
                        <span>No products found.</span>
                     </div>
                  </td>
               </tr>
            </tbody>
            <tbody v-else>
               <template v-for="product in productList" :key="product.id">
                  <tr :class="{ 'pp-parent-row': product.children && product.children.length }">
                     <td :title="product.display_name" class="pp-cell-name">
                        <button v-if="product.children && product.children.length" 
                                class="pp-expand-btn"
                                @click="product.is_expanded = !product.is_expanded">
                           <i :class="product.is_expanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
                        </button>
                        <span class="pp-product-name">{{ truncateText(product.display_name, 30) }}</span>
                     </td>

                     <td>
                        <span v-if="getAuthItemCode(product)" class="pp-code-badge" :title="getAuthItemCode(product)">
                           {{ getAuthItemCode(product) }}
                        </span>
                        <span v-else class="pp-na">N/A</span>
                     </td>
                     
                     <td :title="product.description" class="pp-cell-desc">
                        <span v-if="isAuthProduct(product)">
                           {{ (product.description != undefined && product.description != null && product.description != "") ? truncateText(product.description, 40) : "None" }}
                        </span>
                        <span v-else class="pp-na">N/A</span>
                     </td>
                     
                     <td>
                        <span v-if="isAuthProduct(product)" class="pp-price-badge">{{ formatCurrency(product.price) }}</span>
                        <span v-else class="pp-na">N/A</span>
                     </td>
                     
                     <td>
                        <label v-if="isAuthProduct(product)" class="pp-checkbox-wrap">
                           <input type="checkbox" :disabled="!$store.getters['auth/isGlobalSuperAdmin']" v-model="product.is_charity_product"
                              @change="handleCharityChange(product, product.is_charity_product)" />
                           <span class="pp-checkbox-visual">
                              <i class="fas fa-check"></i>
                           </span>
                        </label>
                        <span v-else class="pp-na">N/A</span>
                     </td>
                     
                     <td>
                        <span v-if="isAuthProduct(product)">{{ product.pricing_plan_type || '-' }}</span>
                        <span v-else class="pp-na">N/A</span>
                     </td>
                     
                     <td>
                        <input type="text" v-if="isAuthProduct(product)" class="pp-inline-select" v-model="product.contract_basis" disabled style="background-color: transparent; border: none;" />
                        <span v-else class="pp-na">N/A</span>
                     </td>
                     
                     <td>
                        <template v-if="isAuthProduct(product)">
                           <span v-if="product.contract_basis === 'Term Renewal' || product.contract_basis === 'Term'">
                              {{ product.renewal_period ? `${product.renewal_period} Months` : '-' }}
                           </span>
                           <span v-else class="pp-na">-</span>
                        </template>
                        <span v-else class="pp-na">N/A</span>
                     </td>
                     
                     <td>
                        <div v-if="isUUID(product.id)">
                           <span v-if="product.auth_item_code" class="pp-linked-badge" :title="getLinkedAuthProductName(product.auth_item_code)">
                              <i class="fas fa-link"></i>
                              {{ truncateText(getLinkedAuthProductName(product.auth_item_code), 30) }}
                           </span>
                           <span v-else class="pp-unlinked-badge">
                              <i class="fas fa-unlink"></i> Not Linked
                           </span>
                        </div>
                        <div v-else>
                           <span class="pp-na">N/A</span>
                        </div>
                     </td>

                     <td v-if="$store.getters['auth/isGlobalSuperAdmin']" class="pp-actions">
                        <div v-if="isUUID(product.id)">
                           <button v-if="product.auth_item_code" type="button" class="pp-action-btn pp-action-btn--unlink"
                              title="Unlink Auth Product" @click="unlinkAuthProduct(product)">
                              <i class="fas fa-unlink"></i>
                           </button>
                           <button v-else type="button" class="pp-action-btn pp-action-btn--link" 
                              title="Link to Auth Product" @click="openLinkAuthProductModal(product)">
                              <i class="fas fa-link"></i>
                           </button>
                        </div>
                     </td>
                  </tr>

                  <template v-if="product.is_expanded && product.children && product.children.length">
                     <tr v-for="child in product.children" :key="child.id" class="pp-child-row">
                        <td :title="child.display_name" class="pp-cell-name" style="padding-left: 3.5rem;">
                           <i class="fas fa-level-up-alt fa-rotate-90 pp-child-icon"></i>
                           <span class="pp-product-name">{{ truncateText(child.display_name, 30) }}</span>
                        </td>
                        <td>
                           <span v-if="getAuthItemCode(child)" class="pp-code-badge" :title="getAuthItemCode(child)">
                              {{ getAuthItemCode(child) }}
                           </span>
                           <span v-else class="pp-na">N/A</span>
                        </td>
                        <td :title="child.description" class="pp-cell-desc">
                           <span v-if="isAuthProduct(child)">
                              {{ (child.description != undefined && child.description != null && child.description != "") ? truncateText(child.description, 40) : "None" }}
                           </span>
                           <span v-else class="pp-na">N/A</span>
                        </td>
                        <td>
                           <span v-if="isAuthProduct(child)" class="pp-price-badge">{{ formatCurrency(child.price) }}</span>
                           <span v-else class="pp-na">N/A</span>
                        </td>
                        <td>
                           <label v-if="isAuthProduct(child)" class="pp-checkbox-wrap">
                              <input type="checkbox" :disabled="!$store.getters['auth/isGlobalSuperAdmin']" v-model="child.is_charity_product"
                                 @change="handleCharityChange(child, child.is_charity_product)" />
                              <span class="pp-checkbox-visual">
                                 <i class="fas fa-check"></i>
                              </span>
                           </label>
                           <span v-else class="pp-na">N/A</span>
                        </td>
                        <td><span class="pp-na">N/A</span></td>
                        <td><span class="pp-na">N/A</span></td>
                        <td><span class="pp-na">N/A</span></td>
                        <td><span class="pp-na">N/A</span></td>
                        <td v-if="$store.getters['auth/isGlobalSuperAdmin']" class="pp-actions"></td>
                     </tr>
                  </template>
               </template>
            </tbody>
         </table>
      </div>
   </div>

   <!-- ── Link Auth Product Modal ── -->
   <Transition name="pp-modal-fade">
      <div v-if="showLinkAuthProductModal" class="pp-modal-overlay" @click.self="closeLinkAuthProductModal">
         <div class="pp-modal">
            <div class="pp-modal__header">
               <h5 class="pp-modal__title"><i class="fas fa-link"></i> Link to Auth Product</h5>
               <button @click="closeLinkAuthProductModal" class="pp-modal__close">&times;</button>
            </div>
            <div class="pp-modal__divider"></div>
            <form @submit.prevent="confirmLinkAuthProduct" class="pp-modal__body">
               <div class="pp-form-group">
                  <label class="pp-form-label">
                     Select an auth product to link to <strong>{{ productToLink?.display_name }}</strong> <span class="pp-required">*</span>
                  </label>
                  <select class="pp-form-input" v-model="selectedAuthProduct" required>
                     <option disabled value="">- Select Auth Product -</option>
                     <option v-for="authProduct in authProductList" :key="authProduct.id" :value="authProduct">
                        {{ authProduct.Product.display_name }}{{ authProduct.Product.display_name !== authProduct.Product.id ? ` (${authProduct.Product.id})` : '' }}
                     </option>
                  </select>
               </div>
               <div class="pp-modal__actions">
                  <button type="button" class="pp-btn pp-btn--ghost" @click="closeLinkAuthProductModal">Cancel</button>
                  <button type="submit" class="pp-btn pp-btn--primary" :disabled="!selectedAuthProduct">
                     <i class="fas fa-link"></i> Link Product
                  </button>
               </div>
            </form>
         </div>
      </div>
   </Transition>

   <ConfirmModal
      v-model="showConfirmModal"
      :title="confirmModalTitle"
      :message="confirmModalMessage"
      @confirm="onConfirmAction"
   />
</template>

<script>
import { debounce } from '@/plugins/helper.js'
import 'vue-multiselect/dist/vue-multiselect.css'
import "@/assets/scss/vue-multiselect.scss";

export default {
   components: {},
   data() {
      return {
         search: "",
         headers: [
            { text: "Product Name", value: "name" },
            { text: "Auth Item Code", value: "auth_item_code" },
            { text: "Product Description", value: "description" },
            { text: "Product Price", value: "price" },
            { text: "Charity Product", value: "charity_product" },
            { text: "Pricing Plan Type", value: "pricing_plan_type" },
            { text: "Contract Type", value: "contract_type" },
            { text: "Contract Term", value: "renewal_period" },
            { text: "Linked Auth Product", value: "linked_auth_product" },
            { text: "Action", value: "action" }
         ],
         allProductList: [], 
         productList: [],    
         contractBasisOptions: ['One Off', 'Term Renewal', 'Subscription', 'Term'],
         authProductList: [],
         showLinkAuthProductModal: false,
         productToLink: null,
         selectedAuthProduct: "",
         debounceFn: null,
         showConfirmModal: false,
         confirmModalTitle: '',
         confirmModalMessage: '',
         pendingUnlinkProduct: null,
         
         pagination: {
            pageSize: 10,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         rowsPerPageList: [
            { text: "10", value: 10 }, 
            { text: "20", value: 20 }, 
            { text: "50", value: 50 }
         ],
         sortByList: [
            { text: "Internal First", value: "internal_first" },
            { text: "Price (Highest to Lowest)", value: "price_desc" },
            { text: "Price (Lowest to Highest)", value: "price_asc" },
            { text: "Name (A-Z)", value: "name_asc" },
            { text: "Name (Z-A)", value: "name_desc" }
         ],
         sortBy: "internal_first",
      }
   },
   computed: {
      paginationLabel() {
         if (this.pagination.totalItems === 0) return "0 - 0";
         const start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems);
         const end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems);
         return `${start} - ${end}`;
      },
      filteredHeaders() {
         if (!this.$store.getters['auth/isGlobalSuperAdmin']) {
            return this.headers.filter(h => h.value !== 'action');
         }
         return this.headers;
      },
      charityProductCount() {
         let count = 0;
         (this.allProductList || []).forEach(p => {
            if (p.is_charity_product) count++;
            if (Array.isArray(p.children)) {
               p.children.forEach(c => { if (c.is_charity_product) count++; });
            }
         });
         return count;
      },
      linkedProductCount() {
         return (this.allProductList || []).filter(p => this.isUUID(p.id) && p.auth_item_code).length;
      },
      avgPrice() {
         const authProducts = [];
         (this.allProductList || []).forEach(p => {
            if (this.isAuthProduct(p) && p.price) authProducts.push(parseFloat(p.price));
            if (Array.isArray(p.children)) {
               p.children.forEach(c => {
                  if (this.isAuthProduct(c) && c.price) authProducts.push(parseFloat(c.price));
               });
            }
         });
         if (!authProducts.length) return '$0.00';
         const avg = authProducts.reduce((sum, v) => sum + v, 0) / authProducts.length;
         return `$${avg.toFixed(2)}`;
      }
   },
   watch: {
      'pagination.pageSize'() { 
         this.pagination.page = 1; 
         this.processClientSideData(); 
      },
      sortBy() { 
         this.pagination.page = 1; 
         this.processClientSideData(); 
      },
      'pagination.page'() { 
         this.processClientSideData(); 
      }
   },
   created() {
      this.init()
      this.debounceFn = debounce(() => this.processClientSideData(), 500)
   },
   methods: {
      async init() {
         await Promise.allSettled([
            this.getAuthProducts(),
            this.getProductList()
         ]);
      },
      async getAuthProducts() {
         try {
            const response = await this.$axios.get("/auth-products");
            this.authProductList = (response && response.data && response.data.authProducts) || [];
         } catch (error) {
            const msg = error?.response?.data?.message || error?.message || "Failed to load auth products";
            if (this.$toast && typeof this.$toast.error === 'function') {
               this.$toast.error(msg);
            } else {
               console.warn(msg, error);
            }
         }
      },
      async getProductList() {
         try {
            const response = await this.$axios.get("/get-product-pricing");
            const rawProducts = [];
            const productPrices = response?.data?.productPrices || [];
            
            productPrices.forEach((p) => {
               rawProducts.push({
                  id: p.ProductID || p.product_id,
                  display_name: p.DisplayName || p.display_name || 'Unnamed Product',
                  price: p.Price !== undefined ? p.Price : (p.price !== undefined ? p.price : 0),
                  description: p.Description || p.description || '',
                  is_charity_product: (p.IsCharityProduct == 1 || p.is_charity_product == 1 || p.IsCharityProduct === true || p.is_charity_product === true) ? true : false,
                  contract_basis: p.ContractBasis || p.contract_basis || '',
                  pricing_plan_type: p.PricingPlanType || p.pricing_plan_type || '',
                  renewal_period: (p.RenewalPeriod !== undefined && p.RenewalPeriod !== null) ? p.RenewalPeriod : ((p.ContractBasis === 'Term Renewal' || p.ContractBasis === 'Term' || p.contract_basis === 'Term Renewal' || p.contract_basis === 'Term') ? 12 : null),
                  auth_item_code: p.AuthItemCode || p.auth_item_code,
                  has_auth_product_linked: (p.HasAuthProductLinked == 1 || p.has_auth_product_linked == 1 || p.HasAuthProductLinked === true || p.has_auth_product_linked === true) ? true : false,
                  parent_product_ids: (() => {
                     const raw = p.ParentProductIDs || p.parent_product_ids || p.ParentProductID || p.parent_product_id || null;
                     if (!raw) return [];
                     if (Array.isArray(raw)) return raw;
                     if (typeof raw === 'string') {
                        try {
                           const parsed = JSON.parse(raw);
                           if (Array.isArray(parsed)) return parsed;
                           if (typeof parsed === 'string' && parsed) return [parsed];
                        } catch(e) {
                           if (raw.trim()) return [raw.trim()];
                        }
                     }
                     return [];
                  })(),
                  is_expanded: true,
                  children: []
               });
            });

            // Build lookup map by id only (auth products use item_code as id)
            const productMap = {};
            rawProducts.forEach(p => {
               if (p.id) {
                  productMap[p.id] = p;
               }
            });

            // Assign children to ALL their parents (supports multi-parent)
            rawProducts.forEach(p => {
               if (Array.isArray(p.parent_product_ids) && p.parent_product_ids.length > 0) {
                  p.parent_product_ids.forEach(parentId => {
                     const parent = productMap[parentId];
                     if (parent && parent.id !== p.id) {
                        if (!Array.isArray(parent.children)) {
                           parent.children = [];
                        }
                        parent.children.push(p);
                     }
                  });
               }
            });

            // Root items = products with no parents, or whose parents aren't in the list
            const rootItems = rawProducts.filter(p => {
               if (!Array.isArray(p.parent_product_ids) || p.parent_product_ids.length === 0) return true;
               return !p.parent_product_ids.some(pid => {
                  const parent = productMap[pid];
                  return parent && parent.id !== p.id;
               });
            });

            console.log("[ProductPricing] Total raw products:", rawProducts.length, "Root items:", rootItems.length);
            this.allProductList = rootItems.length > 0 ? rootItems : rawProducts;
            this.processClientSideData();
         } catch (error) {
            const msg = error?.response?.data?.message || error?.message || "Failed to load product pricing list";
            if (this.$toast && typeof this.$toast.error === 'function') {
               this.$toast.error(msg);
            } else {
               console.error(msg, error);
            }
         }
      },
      processClientSideData() {
         let processedList = [...this.allProductList];
         
         if (this.search) {
            const searchTerm = this.search.toLowerCase();
            processedList = processedList.filter(p => {
               const pCode = this.getAuthItemCode(p);
               const parentMatches = (p.display_name && p.display_name.toLowerCase().includes(searchTerm)) ||
                                     (p.description && p.description.toLowerCase().includes(searchTerm)) ||
                                     (pCode && pCode.toLowerCase().includes(searchTerm));
                                     
               const childMatches = Array.isArray(p.children) && p.children.some(c => {
                  const cCode = this.getAuthItemCode(c);
                  return (c.display_name && c.display_name.toLowerCase().includes(searchTerm)) ||
                         (c.description && c.description.toLowerCase().includes(searchTerm)) ||
                         (cCode && cCode.toLowerCase().includes(searchTerm));
               });
               
               if (childMatches) {
                  p.is_expanded = true;
               }
               
               return parentMatches || childMatches;
            });
         }

         processedList.sort((a, b) => {
            const aIsInternal = this.isUUID(a.id);
            const bIsInternal = this.isUUID(b.id);

            switch (this.sortBy) {
               case 'internal_first': {
                  if (aIsInternal === bIsInternal) {
                     return (a.display_name || '').localeCompare(b.display_name || '');
                  }
                  return aIsInternal ? -1 : 1;
               }
               case 'price_desc': {
                  if (aIsInternal !== bIsInternal) return aIsInternal ? 1 : -1;
                  return (b.price || 0) - (a.price || 0);
               }
               case 'price_asc': {
                  if (aIsInternal !== bIsInternal) return aIsInternal ? 1 : -1;
                  return (a.price || 0) - (b.price || 0);
               }
               case 'name_asc':
                  return (a.display_name || '').localeCompare(b.display_name || '');
               case 'name_desc':
                  return (b.display_name || '').localeCompare(a.display_name || '');
               default:
                  return 0;
            }
         });

         this.pagination.totalItems = processedList.length;
         this.pagination.totalPages = Math.ceil(processedList.length / this.pagination.pageSize) || 1;
         
         if (this.pagination.page > this.pagination.totalPages) {
            this.pagination.page = this.pagination.totalPages;
         }
         
         const start = (this.pagination.page - 1) * this.pagination.pageSize;
         const end = start + this.pagination.pageSize;
         
         this.productList = processedList.slice(start, end);
      },
      onSearch() {
         this.pagination.page = 1;
         this.debounceFn();
      },
      previousPage() {
         if (this.pagination.page > 1) { 
            this.pagination.page--; 
         } 
      },
      nextPage() { 
         if (this.pagination.page < this.pagination.totalPages) { 
            this.pagination.page++; 
         } 
      },
      isAuthProduct(product) {
         return !this.isUUID(product.id);
      },
      getAuthItemCode(product) {
         if (!product) return null;
         if (product.auth_item_code && !this.isUUID(product.auth_item_code)) {
            return product.auth_item_code;
         }
         if (product.id && !this.isUUID(product.id)) {
            return product.id;
         }
         return null;
      },
      async savePriceOrDescription(item) {
         const numericValue = parseFloat(item['price']);
         item['product_id'] = item.id
         item['price'] = isNaN(numericValue) ? 0.00 : numericValue.toFixed(2)
         item['price_2'] = isNaN(numericValue) ? 0.00 : numericValue.toFixed(2)

         await this.$axios.post("/product-pricing", item)
            .then((response) => {
               if (response.data.success) {
                  this.$toast.success(response.data.message)
                  this.getProductList()
               } else {
                  this.$toast.error(response.data.message)
               }
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },
      formatCurrency(value) {
         const numericValue = parseFloat(value);
         if (isNaN(numericValue)) {
            return '$0.00';
         }
         return `$${numericValue.toFixed(2)}`;
      },
      async handleCharityChange(product, bool_change) {
         product['is_charity_product'] = bool_change
         this.savePriceOrDescription(product)
      },
      handleRenewalPeriodChange(product) {
         this.savePriceOrDescription(product);
      },
      truncateText(text, length) {
         if (text && text.length > length) {
            return text.substring(0, length) + '...';
         }
         return text;
      },
      isUUID(uuid) {
         if (typeof uuid !== 'string') return false;
         if (uuid.length !== 36) return false;
         if (uuid[8] !== '-' || uuid[13] !== '-' || uuid[18] !== '-' || uuid[23] !== '-') return false;
         return true;
      },
      getLinkedAuthProductName(auth_item_code) {
         if (!auth_item_code) return null;
         const authProductMatch = this.authProductList.find(a => a.Product.id === auth_item_code);
         return authProductMatch ? authProductMatch.Product.display_name : auth_item_code;
      },
      openLinkAuthProductModal(product) {
         this.productToLink = product;
         this.selectedAuthProduct = "";
         this.showLinkAuthProductModal = true;
      },
      closeLinkAuthProductModal() {
         this.showLinkAuthProductModal = false;
         this.productToLink = null;
         this.selectedAuthProduct = "";
      },
      unlinkAuthProduct(product) {
         this.pendingUnlinkProduct = product;
         this.confirmModalTitle = 'Unlink Auth Product';
         this.confirmModalMessage = `Are you sure you want to unlink the auth product from ${product.display_name}?`;
         this.showConfirmModal = true;
      },
      onConfirmAction() {
         const product = this.pendingUnlinkProduct;
         this.pendingUnlinkProduct = null;
         if (!product) return;
         product.auth_item_code = null;
         product.has_auth_product_linked = false;
         this.savePriceOrDescription(product);
      },
      confirmLinkAuthProduct() {
         if (!this.selectedAuthProduct) return;
         
         this.productToLink.auth_item_code = this.selectedAuthProduct.Product.id;
         this.productToLink.has_auth_product_linked = true;

         this.savePriceOrDescription(this.productToLink);
         this.closeLinkAuthProductModal();
      }
   }
}
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════
   PRODUCT PRICING — Premium Redesign (Vertex Blue)
   ═══════════════════════════════════════════════════════════ */

/* ── Summary Cards ── */
.pp-summary-row {
   display: grid;
   grid-template-columns: repeat(4, 1fr);
   gap: 16px;
   margin-bottom: 20px;
}

.pp-stat-card {
   background: #fff;
   border-radius: 14px;
   padding: 20px;
   display: flex;
   align-items: center;
   gap: 16px;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
   transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.pp-stat-card:hover {
   transform: translateY(-2px);
   box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.pp-stat-icon {
   width: 48px;
   height: 48px;
   border-radius: 12px;
   display: flex;
   align-items: center;
   justify-content: center;
   color: #fff;
   font-size: 1.15rem;
   flex-shrink: 0;
}

.pp-stat-body {
   display: flex;
   flex-direction: column;
}

.pp-stat-value {
   font-size: 1.6rem;
   font-weight: 700;
   color: #1e293b;
   line-height: 1.2;
}

.pp-stat-label {
   font-size: 0.8rem;
   color: #94a3b8;
   font-weight: 500;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   margin-top: 2px;
}

/* ── Main Card ── */
.pp-card {
   background: #fff;
   border-radius: 16px;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
   overflow: visible;
}

/* ── Toolbar ── */
.pp-card__toolbar {
   display: flex;
   align-items: center;
   justify-content: space-between;
   flex-wrap: wrap;
   gap: 12px;
   padding: 16px 20px;
   border-bottom: 1px solid #f1f5f9;
}

.pp-search-wrap {
   position: relative;
   min-width: 260px;
}

.pp-search-input {
   width: 100%;
   padding: 9px 12px;
   border: 1.5px solid #e2e8f0;
   border-radius: 10px;
   font-size: 0.875rem;
   color: #334155;
   background: #f8fafc;
   transition: border-color 0.2s, box-shadow 0.2s, background-color 0.2s;
   outline: none;
}

.pp-search-input:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.12);
   background: #fff;
}

.pp-toolbar-right {
   display: flex;
   align-items: center;
   flex-wrap: wrap;
   gap: 14px;
}

.pp-toolbar-group {
   display: flex;
   align-items: center;
   gap: 6px;
}

.pp-toolbar-label {
   font-size: 0.8rem;
   font-weight: 600;
   color: #64748b;
   text-transform: uppercase;
   letter-spacing: 0.4px;
}

.pp-select {
   padding: 6px 28px 6px 10px;
   border: 1.5px solid #e2e8f0;
   border-radius: 8px;
   font-size: 0.85rem;
   color: #00A0C1;
   font-weight: 600;
   background: #f8fafc;
   cursor: pointer;
   outline: none;
   transition: border-color 0.2s;
   appearance: auto;
}

.pp-select:focus {
   border-color: #00A0C1;
}

.pp-select--narrow {
   min-width: 80px;
   width: auto;
}

/* ── Pagination ── */
.pp-pagination {
   display: inline-flex;
   align-items: center;
   border: 1.5px solid #e2e8f0;
   border-radius: 10px;
   overflow: visible;
   background: #f8fafc;
}

.pp-page-btn {
   background: transparent;
   border: none;
   cursor: pointer;
   color: #00A0C1;
   font-size: 0.85rem;
   padding: 7px 12px;
   transition: background-color 0.15s;
}

.pp-page-btn:hover:not(:disabled) {
   background: #e6f7fa;
}

.pp-page-btn:disabled {
   color: #cbd5e1;
   cursor: not-allowed;
}

.pp-page-label {
   font-size: 0.82rem;
   color: #64748b;
   font-weight: 500;
   padding: 0 6px;
   white-space: nowrap;
}

.pp-page-label strong {
   color: #1e293b;
}

/* ── Table ── */
.pp-table-wrap {
   overflow-x: auto;
}

.pp-table {
   width: 100%;
   border-collapse: collapse;
}

.pp-table thead tr {
   background: linear-gradient(135deg, #f8fafc 0%, #e6f7fa 100%);
}

.pp-table th {
   padding: 13px 16px;
   text-align: left;
   font-size: 0.75rem;
   font-weight: 700;
   color: #475569;
   text-transform: uppercase;
   letter-spacing: 0.6px;
   border-bottom: 2px solid #e2e8f0;
   white-space: nowrap;
}

.pp-table td {
   padding: 12px 16px;
   font-size: 0.875rem;
   color: #334155;
   border-bottom: 1px solid #f1f5f9;
   vertical-align: middle;
}

.pp-table tbody tr {
   transition: background-color 0.15s ease;
}

.pp-table tbody tr:hover {
   background-color: #f5fcfd;
}

.pp-table tbody tr:last-child td {
   border-bottom: none;
}

/* ── Row Types ── */
.pp-parent-row {
   font-weight: 500;
}

.pp-child-row {
   background-color: #fafbfc;
}

.pp-child-row:hover {
   background-color: #f0f9fa !important;
}

.pp-child-icon {
   color: #00A0C1;
   margin-right: 12px;
   font-size: 0.8rem;
}

/* ── Cell Styles ── */
.pp-cell-name {
   font-weight: 600;
   color: #1e293b;
   white-space: nowrap;
}

.pp-product-name {
   white-space: nowrap;
   vertical-align: middle;
}

.pp-cell-desc {
   max-width: 240px;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
   color: #64748b;
}

.pp-na {
   color: #cbd5e1;
   font-style: italic;
   font-size: 0.82rem;
}

/* ── Expand Button ── */
.pp-expand-btn {
   background: none;
   border: none;
   cursor: pointer;
   color: #00A0C1;
   font-size: 0.75rem;
   width: 24px;
   height: 24px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   border-radius: 6px;
   transition: background-color 0.15s;
   flex-shrink: 0;
   vertical-align: middle;
   margin-right: 4px;
}

.pp-expand-btn:hover {
   background: #e6f7fa;
}

.pp-expand-spacer {
   display: inline-block;
   width: 24px;
   flex-shrink: 0;
}

/* ── Price Badge ── */
.pp-price-badge {
   display: inline-block;
   padding: 3px 10px;
   background: #f0fdf4;
   color: #16a34a;
   border-radius: 16px;
   font-size: 0.82rem;
   font-weight: 700;
   font-family: 'SF Mono', 'Fira Code', monospace;
}

/* ── Code Badge ── */
.pp-code-badge {
   font-family: 'SF Mono', 'Fira Code', 'Roboto Mono', monospace;
   font-size: 0.76rem;
   background: #f1f5f9;
   color: #334155;
   padding: 3px 8px;
   border-radius: 6px;
   border: 1px solid #e2e8f0;
   font-weight: 600;
   display: inline-block;
   letter-spacing: 0.2px;
}

/* ── Checkbox ── */
.pp-checkbox-wrap {
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   justify-content: center;
}

.pp-checkbox-wrap input {
   display: none;
}

.pp-checkbox-visual {
   width: 22px;
   height: 22px;
   border-radius: 6px;
   border: 2px solid #cbd5e1;
   display: flex;
   align-items: center;
   justify-content: center;
   transition: all 0.2s ease;
   color: transparent;
   font-size: 0.7rem;
}

.pp-checkbox-wrap input:checked + .pp-checkbox-visual {
   background: #00A0C1;
   border-color: #00A0C1;
   color: #fff;
}

.pp-checkbox-wrap:hover .pp-checkbox-visual {
   border-color: #00A0C1;
}

/* ── Inline Select ── */
.pp-inline-select {
   padding: 5px 8px;
   border: 1.5px solid #e2e8f0;
   border-radius: 8px;
   font-size: 0.82rem;
   color: #475569;
   background: #f8fafc;
   cursor: pointer;
   outline: none;
   transition: border-color 0.2s;
   min-width: 90px;
}

.pp-inline-select:focus {
   border-color: #00A0C1;
}

.pp-inline-select:disabled {
   background: #f1f5f9;
   color: #94a3b8;
   cursor: not-allowed;
}

/* ── Linked / Unlinked Badges ── */
.pp-linked-badge {
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 4px 10px;
   background: #ecfdf5;
   color: #059669;
   border-radius: 16px;
   font-size: 0.78rem;
   font-weight: 600;
   white-space: nowrap;
}

.pp-linked-badge i {
   font-size: 0.7rem;
}

.pp-unlinked-badge {
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 4px 10px;
   background: #fef2f2;
   color: #dc2626;
   border-radius: 16px;
   font-size: 0.78rem;
   font-weight: 600;
   white-space: nowrap;
}

.pp-unlinked-badge i {
   font-size: 0.7rem;
}

/* ── Action Buttons ── */
.pp-actions {
   text-align: right;
   white-space: nowrap;
}

.pp-action-btn {
   width: 36px;
   height: 36px;
   border-radius: 10px;
   border: none;
   cursor: pointer;
   font-size: 0.82rem;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.pp-action-btn:hover {
   transform: translateY(-1px);
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.pp-action-btn--link {
   background: #e6f7fa;
   color: #00A0C1;
}

.pp-action-btn--link:hover {
   background: #00A0C1;
   color: #fff;
}

.pp-action-btn--unlink {
   background: #fef2f2;
   color: #dc2626;
}

.pp-action-btn--unlink:hover {
   background: #dc2626;
   color: #fff;
}

/* ── Empty State ── */
.pp-empty-row {
   text-align: center;
   padding: 48px 16px !important;
}

.pp-empty-state {
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 8px;
   color: #94a3b8;
}

.pp-empty-state i {
   font-size: 2rem;
   color: #cbd5e1;
}

.pp-empty-state span {
   font-size: 0.9rem;
}

/* ═══════════════════════════════════════════════════════════
   MODAL
   ═══════════════════════════════════════════════════════════ */

.pp-modal-overlay {
   position: fixed;
   top: 0;
   left: 0;
   width: 100vw;
   height: 100vh;
   background: rgba(15, 23, 42, 0.5);
   backdrop-filter: blur(4px);
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 1050;
}

.pp-modal {
   background: #fff;
   border-radius: 20px;
   width: 100%;
   max-width: 520px;
   min-width: 340px;
   box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
   overflow: hidden;
   animation: pp-modal-enter 0.25s ease-out;
}

@keyframes pp-modal-enter {
   from {
      opacity: 0;
      transform: translateY(12px) scale(0.98);
   }
   to {
      opacity: 1;
      transform: translateY(0) scale(1);
   }
}

.pp-modal__header {
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 20px 24px 0;
}

.pp-modal__title {
   font-weight: 700;
   font-size: 1.15rem;
   color: #1e293b;
   display: flex;
   align-items: center;
   gap: 8px;
   margin: 0;
}

.pp-modal__title i {
   color: #00A0C1;
}

.pp-modal__close {
   background: none;
   border: none;
   font-size: 1.8rem;
   color: #94a3b8;
   cursor: pointer;
   line-height: 1;
   padding: 0;
   transition: color 0.15s;
}

.pp-modal__close:hover {
   color: #ef4444;
}

.pp-modal__divider {
   height: 1px;
   background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
   margin: 16px 24px 0;
}

.pp-modal__body {
   padding: 20px 24px 24px;
}

.pp-modal__actions {
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 24px;
   padding-top: 16px;
   border-top: 1px solid #f1f5f9;
}

/* ── Form Controls ── */
.pp-form-group {
   margin-bottom: 16px;
}

.pp-form-label {
   display: block;
   font-weight: 500;
   font-size: 0.875rem;
   color: #475569;
   margin-bottom: 8px;
}

.pp-required {
   color: #ef4444;
}

.pp-form-input {
   width: 100%;
   padding: 10px 14px;
   border: 1.5px solid #e2e8f0;
   border-radius: 10px;
   font-size: 0.875rem;
   color: #334155;
   background: #fff;
   transition: border-color 0.2s, box-shadow 0.2s;
   outline: none;
}

.pp-form-input:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.12);
}

/* ── Buttons ── */
.pp-btn {
   padding: 10px 22px;
   border-radius: 10px;
   font-size: 0.875rem;
   font-weight: 600;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   gap: 6px;
   transition: all 0.2s ease;
   border: none;
}

.pp-btn--primary {
   background: linear-gradient(135deg, #00A0C1, #33b5d1);
   color: #fff;
   box-shadow: 0 2px 8px rgba(0, 160, 193, 0.3);
}

.pp-btn--primary:hover {
   background: linear-gradient(135deg, #008da8, #00A0C1);
   box-shadow: 0 4px 16px rgba(0, 160, 193, 0.4);
   transform: translateY(-1px);
}

.pp-btn--primary:disabled {
   opacity: 0.5;
   cursor: not-allowed;
   transform: none;
}

.pp-btn--ghost {
   background: transparent;
   color: #64748b;
   border: 1.5px solid #e2e8f0;
}

.pp-btn--ghost:hover {
   background: #f8fafc;
   color: #334155;
}

/* ── Modal Transition ── */
.pp-modal-fade-enter-active,
.pp-modal-fade-leave-active {
   transition: opacity 0.2s ease;
}

.pp-modal-fade-enter-from,
.pp-modal-fade-leave-to {
   opacity: 0;
}

/* ── Responsive ── */
@media (max-width: 992px) {
   .pp-summary-row {
      grid-template-columns: repeat(2, 1fr);
   }
}

@media (max-width: 576px) {
   .pp-summary-row {
      grid-template-columns: 1fr;
   }

   .pp-card__toolbar {
      flex-direction: column;
      align-items: stretch;
   }

   .pp-search-wrap {
      min-width: unset;
   }

   .pp-toolbar-right {
      justify-content: space-between;
   }
}
</style>